// 図形の背景色（fill）の選好を localStorage に保存する。
// 付箋・ペンと同じく hex 文字列（"#RRGGBB"）で色を扱い、組み込みプリセットと利用者が
// 追加したカスタム色（最大 SHAPE_FILL_CUSTOM_COLOR_LIMIT 件）から選ぶ。
// 既定は「背景なし」（SHAPE_FILL_NONE）で、従来どおり塗りのない図形を配置する。

import { isValidHexColor, normalizeHexColor } from "./pen-preference";

const FILL_KEY = "refinement-canvas:shape:fill";
const CUSTOM_COLORS_KEY = "refinement-canvas:shape:custom-colors";

// 図形の背景色は hex 文字列、または「背景なし」を表すセンチネル。
export type ShapeFill = string;

/** 「背景なし」を表すセンチネル値。図形作成時は fill を未設定にする。 */
export const SHAPE_FILL_NONE = "none";

// 図形の輪郭が見えるよう、薄め（淡彩）のプリセットを用意する。付箋プリセットと近い系統。
export const SHAPE_FILL_PRESET_COLORS: readonly string[] = [
  "#fff5b3",
  "#ffd6e7",
  "#dbeafe",
  "#dcfce7",
  "#ede9fe",
  "#f3f4f6",
];

const DEFAULT_FILL: ShapeFill = SHAPE_FILL_NONE;

/** ローカル保持できる図形背景色のカスタム色の上限。 */
export const SHAPE_FILL_CUSTOM_COLOR_LIMIT = 12;

/**
 * 図形の背景色の選好を読み込む。
 * 保存値が "none"（背景なし）または有効な hex のときだけ採用し、それ以外は既定（背景なし）。
 */
export function loadShapeFill(): ShapeFill {
  try {
    const value = window.localStorage.getItem(FILL_KEY);
    if (value === SHAPE_FILL_NONE) return SHAPE_FILL_NONE;
    if (isValidHexColor(value)) return normalizeHexColor(value);
    return DEFAULT_FILL;
  } catch {
    return DEFAULT_FILL;
  }
}

export function saveShapeFill(fill: ShapeFill): void {
  try {
    if (fill === SHAPE_FILL_NONE) {
      window.localStorage.setItem(FILL_KEY, SHAPE_FILL_NONE);
      return;
    }
    if (!isValidHexColor(fill)) return;
    window.localStorage.setItem(FILL_KEY, normalizeHexColor(fill));
  } catch {
    // localStorage が使えない環境では保存のみ諦める。
  }
}

/** 追加保存された図形背景色のカスタム色一覧を読み込む。壊れた値は読み飛ばす。 */
export function loadShapeCustomColors(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(CUSTOM_COLORS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const result: string[] = [];
    for (const value of parsed) {
      if (isValidHexColor(value)) {
        const normalized = normalizeHexColor(value);
        if (!result.includes(normalized)) result.push(normalized);
      }
      if (result.length >= SHAPE_FILL_CUSTOM_COLOR_LIMIT) break;
    }
    return result;
  } catch {
    return [];
  }
}

/**
 * 図形背景色のカスタム色を 1 件追加する。
 * - 組み込みプリセットに含まれる色は重複としてカスタム側へは追加しない。
 * - 既にカスタム色に存在する場合は追加せず、現在のリストを返す。
 * - 上限 {@link SHAPE_FILL_CUSTOM_COLOR_LIMIT} に達している場合は古い方を 1 件捨てる（FIFO）。
 *
 * 返り値は更新後のカスタム色一覧。
 */
export function addShapeCustomColor(color: string): readonly string[] {
  if (!isValidHexColor(color)) return loadShapeCustomColors();
  const normalized = normalizeHexColor(color);
  if (SHAPE_FILL_PRESET_COLORS.includes(normalized)) return loadShapeCustomColors();
  const current = loadShapeCustomColors();
  if (current.includes(normalized)) return current;
  const next = [...current, normalized];
  while (next.length > SHAPE_FILL_CUSTOM_COLOR_LIMIT) next.shift();
  try {
    window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}
